import HandshakeOutlined from '@mui/icons-material/HandshakeOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Checkbox } from '@openx/design-system/components/Checkbox/Checkbox.tsx';
import { TableBody } from '@openx/design-system/components/Table/TableBody.tsx';
import { TableCell } from '@openx/design-system/components/Table/TableCell.tsx';
import { TableHead } from '@openx/design-system/components/Table/TableHead.tsx';
import { TableRow } from '@openx/design-system/components/Table/TableRow.tsx';
import { useTranslation } from 'react-i18next';
import { OpenXAgentChip } from '../../shared/OpenXAgentChip.tsx';
import { OpenXAgentSelectAllCheckbox } from '../../shared/OpenXAgentSelectAllCheckbox.tsx';
import { OpenXAgentTable, OpenXAgentTableEmptyRow } from '../../shared/OpenXAgentTable.tsx';
import { OpenXAgentTableSortLabel } from '../../shared/OpenXAgentTableSortLabel.tsx';
import { usePagination } from '../../shared/utils/usePagination.ts';
import { getSortDirection, useSorting } from '../../shared/utils/useSorting.ts';
import { SORT_ACCESSORS, TABLE_COLUMNS, TABLE_MIN_WIDTH } from '../utils/consts.ts';
import type { OpenXAgentMatchedDomainApp } from '../utils/types.ts';

type MatchedDomainAppTableProps = {
  items: OpenXAgentMatchedDomainApp[];
  selectedIds: Set<string>;
  onToggleItem: (id: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
};

export const MatchedDomainAppTable = ({
  items,
  selectedIds,
  onToggleItem,
  onSelectAll,
  onDeselectAll,
}: MatchedDomainAppTableProps) => {
  const { t } = useTranslation();
  const { sortedItems, sorting } = useSorting(items, SORT_ACCESSORS);
  const { pageItems, pagination } = usePagination(sortedItems);

  return (
    <OpenXAgentTable
      ariaLabel={t('Matched domains and app bundles')}
      dataTest="openx-agent-matched-domain-app-table"
      columns={TABLE_COLUMNS}
      minWidth={TABLE_MIN_WIDTH}
      pagination={pagination}
    >
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <OpenXAgentSelectAllCheckbox
                itemCount={items.length}
                selectedCount={items.filter(({ id }) => selectedIds.has(id)).length}
                ariaLabel={t('Select all domains and app bundles')}
                onSelectAll={onSelectAll}
                onDeselectAll={onDeselectAll}
                menuItems={[
                  { label: t('Select all'), onClick: onSelectAll },
                  { label: t('Deselect all'), onClick: onDeselectAll },
                ]}
              />
            </Box>
          </TableCell>

          <TableCell sortDirection={getSortDirection(sorting, 'name')}>
            <OpenXAgentTableSortLabel sorting={sorting} columnId="name">
              {t('Name')}
            </OpenXAgentTableSortLabel>
          </TableCell>

          <TableCell sortDirection={getSortDirection(sorting, 'spend')}>
            <OpenXAgentTableSortLabel sorting={sorting} columnId="spend">
              {t('30d Spend')}
            </OpenXAgentTableSortLabel>
          </TableCell>

          <TableCell sortDirection={getSortDirection(sorting, 'marketRequest')}>
            <OpenXAgentTableSortLabel sorting={sorting} columnId="marketRequest">
              {t('Market Request')}
            </OpenXAgentTableSortLabel>
          </TableCell>

          <TableCell sortDirection={getSortDirection(sorting, 'relevance')}>
            <OpenXAgentTableSortLabel sorting={sorting} columnId="relevance">
              {t('Relevance')}
            </OpenXAgentTableSortLabel>
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {pageItems.map(({ id, name, isDirect, spend, marketRequest, relevance }) => (
          <TableRow hover key={id} data-test={`openx-agent-matched-domain-app-row-${id}`}>
            <TableCell padding="checkbox">
              <Checkbox
                checked={selectedIds.has(id)}
                onChange={() => onToggleItem(id)}
                slotProps={{ input: { 'aria-label': t('Select {name}', { name }) } }}
              />
            </TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                <Typography component="span" noWrap sx={{ minWidth: 0, font: 'inherit', fontWeight: 'fontWeightBold' }}>
                  {name}
                </Typography>
                {isDirect ? (
                  <OpenXAgentChip
                    icon={HandshakeOutlined}
                    data-test="openx-agent-matched-domain-app-direct-chip"
                    sx={{ ml: 1, flexShrink: 0, backgroundColor: 'info.light', color: 'info.main' }}
                  >
                    {t('Direct')}
                  </OpenXAgentChip>
                ) : null}
              </Box>
            </TableCell>
            <TableCell>{spend}</TableCell>
            <TableCell>{marketRequest}</TableCell>
            <TableCell>{relevance}</TableCell>
          </TableRow>
        ))}

        {items.length === 0 ? (
          <OpenXAgentTableEmptyRow colSpan={TABLE_COLUMNS.length}>
            {t('No domains or app bundles found')}
          </OpenXAgentTableEmptyRow>
        ) : null}
      </TableBody>
    </OpenXAgentTable>
  );
};
